/**
 * BusinessSignupV2 — orchestrator for the v2 onboarding flow.
 *
 * Route: /magic-onboarding-v2
 * Steps (1-indexed, ?step= param):
 *   1 Account  2 Business  3 Services  4 Schedule  5 Branding
 *   6 Done (never via URL — triggered by Launch on step 5)
 *
 * Invariants:
 *  - State type is BusinessSignupData (canonical, shared with v1)
 *  - Saves to Supabase only once, on Launch (step 5 Branding CTA)
 *  - localStorage via saveV2Draft / loadV2Draft
 *  - Stripe & Calendar are activated from the dashboard after launch, not here
 */
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useBusinessProfile } from '@/hooks/useMagicBusinessProfile';
import { loadV2Draft, saveV2Draft, clearV2Draft, deriveV2ResumeStep, V2_STEPS, type V2StepNumber, type V2Draft } from '@/utils/v2OnboardingDraft';
import { v2Slugify } from '@/components/onboarding/onboardingDefaults';
import { recordLaunchFailed, recordLaunchSucceeded } from './onboardingFlow';
import { V2GlobalStyles } from './shared/primitives';
import { defaultFormData } from '@/components/onboarding/onboardingDefaults';
import V2ResumeBanner from './shared/V2ResumeBanner';
import V2SaveError from './shared/V2SaveError';
import type { BusinessSignupData } from '@/components/onboarding/magic-steps/BusinessSignup';

// Step components
import V2AccountStep from './steps/V2AccountStep';
import V2BusinessStep from './steps/V2BusinessStep';
import V2ServicesStep from './steps/V2ServicesStep';
import V2ScheduleStep from './steps/V2ScheduleStep';
import V2BrandingStep from './steps/V2BrandingStep';
import V2DoneStep from './steps/V2DoneStep';

// 5 input steps; DONE (6) is never a URL step
const TOTAL_STEPS = 5;

function clampStep(n: number): V2StepNumber {
  // DONE (6) is never reachable via URL — clamp to 1-5
  return Math.max(1, Math.min(5, Math.round(n))) as V2StepNumber;
}

export default function BusinessSignupV2() {
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { saveBusinessProfile, isLoading: isSaving } = useBusinessProfile();

  // ── State ────────────────────────────────────────────────────────────────
  const hasDraftRef = useRef(false);
  // V2Draft rather than BusinessSignupData so the pre-signup theme choice is
  // typed all the way through to saveBusinessProfile instead of riding along
  // untyped and getting dropped by a future refactor.
  const [formData, setFormData] = useState<V2Draft>(() => {
    const draft = loadV2Draft();
    hasDraftRef.current = draft !== null;
    return draft ?? { ...defaultFormData };
  });
  const [step, setStep] = useState<V2StepNumber>(1);
  const [animDir, setAnimDir] = useState<'forward' | 'back'>('forward');
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const [resumeBannerSavedAt, setResumeBannerSavedAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState(false);
  const [validationError, setValidationError] = useState<string>('');
  const hasInitialized = useRef(false);

  // ── Init: resolve starting step ──────────────────────────────────────────
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const rawStep = parseInt(searchParams.get('step') ?? '0', 10);

    if (hasDraftRef.current) {
      if (rawStep >= 1 && rawStep <= 5) {
        setStep(clampStep(rawStep));
      } else {
        const resumeStep = deriveV2ResumeStep(formData);
        setStep(resumeStep);
        if (resumeStep > 1) {
          try {
            const savedRaw = localStorage.getItem('businessSignupData');
            const parsed = JSON.parse(savedRaw ?? '{}');
            const savedAt = parsed._savedAt as string | undefined;
            if (savedAt) setResumeBannerSavedAt(savedAt);
          } catch { /* ignore */ }
          setShowResumeBanner(true);
        }
      }
    } else {
      if (rawStep >= 1 && rawStep <= 5) {
        setStep(clampStep(rawStep));
      } else {
        setStep(V2_STEPS.ACCOUNT);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auth guard: unauthenticated users can't access steps beyond Account ──
  useEffect(() => {
    if (authLoading) return;
    if (!user && step > V2_STEPS.ACCOUNT && step !== V2_STEPS.DONE) {
      goToStep(V2_STEPS.ACCOUNT, 'back');
    }
   
  }, [user, authLoading, step]);

  // ── Auth listener: auto-advance step 1 → 2 when user signs in ───────────
  useEffect(() => {
    if (!user) return;
    if (step === V2_STEPS.ACCOUNT) {
      setFormData(prev => ({
        ...prev,
        email: prev.email || user.email || '',
        ssoConnected: true,
      }));
      goToStep(V2_STEPS.BUSINESS, 'forward');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Persist draft on formData change ────────────────────────────────────
  useEffect(() => {
    try {
      const toSave = { ...formData, _savedAt: new Date().toISOString(), logoFile: null };
      localStorage.setItem('businessSignupData', JSON.stringify(toSave));
    } catch { /* storage full — ignore */ }
  }, [formData]);

  // ── Sync step to URL ─────────────────────────────────────────────────────
  useEffect(() => {
    if (step === V2_STEPS.DONE) return;
    setSearchParams({ step: String(step) }, { replace: true });
  }, [step, setSearchParams]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  function update(patch: Partial<BusinessSignupData>) {
    setFormData(prev => ({ ...prev, ...patch }));
    setValidationError('');
  }

  function goToStep(target: V2StepNumber, dir: 'forward' | 'back') {
    setAnimDir(dir);
    setStep(target);
    setValidationError('');
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  function handleBack() {
    if (step <= 1) return;
    goToStep((step - 1) as V2StepNumber, 'back');
  }

  function handleNext() {
    const err = validateStep(step);
    if (err) { setValidationError(err); return; }
    if (step < V2_STEPS.BRANDING) goToStep((step + 1) as V2StepNumber, 'forward');
    // V2_STEPS.BRANDING triggers handleLaunch via its own onLaunch prop
  }

  // ── Validation ───────────────────────────────────────────────────────────
  function validateStep(s: V2StepNumber): string {
    switch (s) {
      case V2_STEPS.ACCOUNT:
        if (!user) return 'Please sign in to continue.';
        return '';
      case V2_STEPS.BUSINESS:
        if (!formData.fullName?.trim()) return 'Full name is required.';
        if (!formData.businessName?.trim()) return 'Business name is required.';
        if (!formData.businessAddress?.trim()) return 'Business address is required.';
        if (!formData.timezone) return 'Timezone is required.';
        if (!formData.bookingUrl?.trim()) return 'Booking URL is required.';
        return '';
      case V2_STEPS.SERVICES:
        if (!formData.services?.length) return 'Add at least one service.';
        if (formData.services.some(s => s.price < 5)) return 'All services must be at least $5.';
        return '';
      case V2_STEPS.SCHEDULE: {
        const hasEnabled = formData.workingHours
          ? Object.values(formData.workingHours).some(d => d?.enabled)
          : false;
        if (!hasEnabled) return 'Enable at least one working day.';
        return '';
      }
      case V2_STEPS.BRANDING:
        if (!formData.title?.trim()) return 'A tagline / title is required.';
        return '';
      default:
        return '';
    }
  }

  function validateAll(): string {
    if (!user) return 'Please sign in.';
    if (!formData.fullName?.trim()) return 'Full name is missing.';
    if (!formData.businessName?.trim()) return 'Business name is missing.';
    if (!formData.businessAddress?.trim()) return 'Business address is missing.';
    if (!formData.bookingUrl?.trim()) return 'Booking URL is missing.';
    if (!formData.services?.length) return 'Add at least one service.';
    if (formData.services.some(s => s.price < 5)) return 'All services must be at least $5.';
    const hasEnabled = formData.workingHours
      ? Object.values(formData.workingHours).some(d => d?.enabled)
      : false;
    if (!hasEnabled) return 'Enable at least one working day.';
    if (!formData.title?.trim()) return 'Tagline / title is missing.';
    return '';
  }

  // ── Launch (save to Supabase) ─────────────────────────────────────────────
  async function handleLaunch() {
    const err = validateAll();
    if (err) { setValidationError(err); return; }

    setSaveError(false);
    try {
      await saveBusinessProfile(formData);
      clearV2Draft();
      recordLaunchSucceeded(user?.id);
      goToStep(V2_STEPS.DONE, 'forward');
    } catch (error) {
      recordLaunchFailed(user?.id, error);
      setSaveError(true);
    }
  }

  // ── Auto-gen booking URL from business name ──────────────────────────────
  function handleBusinessNameChange(name: string) {
    const shouldAutoGen = !formData.bookingUrl || formData.bookingUrl === v2Slugify(formData.businessName);
    update({
      businessName: name,
      bookingUrl: shouldAutoGen ? v2Slugify(name) : formData.bookingUrl,
    });
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const sharedStepProps = {
    formData,
    update,
    onBack: step > 1 ? handleBack : undefined,
    onNext: handleNext,
    validationError,
    // Account (step 1) gets stepIdx=-1 → V2Step hides rail + label.
    // Business..Branding (steps 2-5) get stepIdx 0-3 → "Step 1-4 of 4".
    stepIdx: step - 2,
    totalSteps: 4,
    animDir,
  };

  return (
    <div style={{
      minHeight: '100dvh',
      background: `radial-gradient(ellipse at 50% -10%, #E3E3FD 0%, #F8F8FF 55%)`,
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 480,
      margin: '0 auto',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <V2GlobalStyles />

      {showResumeBanner && step !== V2_STEPS.DONE && (
        <V2ResumeBanner
          onDismiss={() => setShowResumeBanner(false)}
          savedAt={resumeBannerSavedAt}
        />
      )}

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={step}
          initial={{ opacity: 0, x: animDir === 'forward' ? 40 : -40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: animDir === 'forward' ? -40 : 40 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}
        >
          {step === V2_STEPS.ACCOUNT && (
            <V2AccountStep {...sharedStepProps} />
          )}
          {step === V2_STEPS.BUSINESS && (
            <V2BusinessStep
              {...sharedStepProps}
              userId={user?.id}
              onBusinessNameChange={handleBusinessNameChange}
            />
          )}
          {step === V2_STEPS.SERVICES && (
            <V2ServicesStep {...sharedStepProps} />
          )}
          {step === V2_STEPS.SCHEDULE && (
            <V2ScheduleStep {...sharedStepProps} />
          )}
          {step === V2_STEPS.BRANDING && (
            <V2BrandingStep
              {...sharedStepProps}
              onLaunch={handleLaunch}
              isSaving={isSaving}
            />
          )}
          {step === V2_STEPS.DONE && (
            <V2DoneStep formData={formData} />
          )}
        </motion.div>
      </AnimatePresence>

      {saveError && (
        <V2SaveError
          onRetry={handleLaunch}
          onDismiss={() => setSaveError(false)}
        />
      )}
    </div>
  );
}
